import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Project, Selection } from '../types'
import { daysBetween, fmtDate, fmtMoney } from '../lib/format'
import Badge, { type BadgeColor } from '../components/Badge'

const STATUS_COLOR: Record<Selection['status'], BadgeColor> = {
  not_started: 'gray',
  researching: 'gold',
  decided: 'gold',
  ordered: 'green',
  installed: 'green',
}

const STATUS_FLOW: Selection['status'][] = [
  'not_started',
  'researching',
  'decided',
  'ordered',
  'installed',
]

export default function SelectionsTab({ project }: { project: Project }) {
  const [items, setItems] = useState<Selection[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<Selection>>({ status: 'not_started' })

  useEffect(() => {
    api.selections.list(project.id).then(setItems)
  }, [project.id])

  const totals = useMemo(() => {
    const allowance = items.reduce((s, i) => s + i.allowance_amount, 0)
    const actual = items.reduce((s, i) => s + (i.actual_amount ?? 0), 0)
    const variance = items
      .filter((i) => i.actual_amount !== null)
      .reduce((s, i) => s + (i.variance_amount ?? 0), 0)
    return { allowance, actual, variance }
  }, [items])

  async function add() {
    if (!draft.category) return
    const item = await api.selections.create(project.id, {
      ...draft,
      allowance_amount: Number(draft.allowance_amount) || 0,
    })
    setItems((p) => [...p, item])
    setAdding(false)
    setDraft({ status: 'not_started' })
  }

  async function advance(s: Selection) {
    const idx = STATUS_FLOW.indexOf(s.status)
    const next = STATUS_FLOW[Math.min(idx + 1, STATUS_FLOW.length - 1)]
    const updated = await api.selections.update(s.id, { status: next })
    setItems((p) => p.map((x) => (x.id === s.id ? updated : x)))
  }

  async function update(s: Selection, patch: Partial<Selection>) {
    const updated = await api.selections.update(s.id, patch)
    setItems((p) => p.map((x) => (x.id === s.id ? updated : x)))
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Selections</h2>
          <p className="muted small">
            {items.length} items · prevents the #1 source of custom-build delays + budget disputes.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ Add'}
        </button>
      </div>

      <div className="stat-grid mb-3">
        <Stat label="Total Allowance" value={fmtMoney(totals.allowance)} />
        <Stat label="Actual (decided)" value={fmtMoney(totals.actual)} />
        <Stat
          label="Variance"
          value={fmtMoney(totals.variance)}
          alert={totals.variance > 0}
        />
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Category</span>
            <input
              value={draft.category ?? ''}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              placeholder="Cabinets, Countertops, Tile, Faucets…"
            />
          </label>
          <label className="form-row">
            <span>Description</span>
            <input
              value={draft.item_description ?? ''}
              onChange={(e) => setDraft({ ...draft, item_description: e.target.value })}
              placeholder="Master bath vanity faucet"
            />
          </label>
          <label className="form-row">
            <span>Allowance</span>
            <input
              type="number"
              value={(draft.allowance_amount as number | undefined) ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, allowance_amount: parseFloat(e.target.value) || 0 })
              }
              placeholder="500"
            />
          </label>
          <label className="form-row">
            <span>Decision deadline</span>
            <input
              type="date"
              value={draft.decision_deadline ?? ''}
              onChange={(e) => setDraft({ ...draft, decision_deadline: e.target.value })}
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add Selection
          </button>
        </div>
      )}

      <div className="card no-pad">
        <div className="sel-grid sel-hdr">
          <div>Category</div>
          <div>Item</div>
          <div className="text-right">Allowance</div>
          <div className="text-right">Actual</div>
          <div className="text-right">Variance</div>
          <div>Deadline</div>
          <div>Status</div>
        </div>
        {items.map((s) => {
          const days = s.decision_deadline ? daysBetween(new Date(), s.decision_deadline) : null
          const overdue = !s.actual_amount && days !== null && days < 0
          return (
            <div key={s.id} className={`sel-grid ${overdue ? 'overdue' : ''}`}>
              <div className="bud-item">{s.category}</div>
              <div className="muted small">{s.item_description ?? '—'}</div>
              <div className="text-right">{fmtMoney(s.allowance_amount)}</div>
              <div className="text-right">
                <ActualCell selection={s} onSave={(v) => update(s, { actual_amount: v })} />
              </div>
              <div
                className={`text-right ${
                  s.variance_amount !== null && s.variance_amount > 0 ? 'danger' : ''
                }`}
              >
                {s.variance_amount === null ? '—' : fmtMoney(s.variance_amount)}
              </div>
              <div className={`muted small ${overdue ? 'danger' : ''}`}>
                {fmtDate(s.decision_deadline)}
                {days !== null && !s.actual_amount && (
                  <div className="small">
                    {days < 0 ? `${Math.abs(days)}d late` : `${days}d`}
                  </div>
                )}
              </div>
              <div>
                <button className="link" onClick={() => advance(s)}>
                  <Badge color={STATUS_COLOR[s.status]}>
                    {s.status.toUpperCase().replace('_', ' ')}
                  </Badge>
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function ActualCell({
  selection,
  onSave,
}: {
  selection: Selection
  onSave: (v: number | null) => Promise<void> | void
}) {
  const [draft, setDraft] = useState<string>(
    selection.actual_amount !== null ? String(selection.actual_amount) : ''
  )
  // Reset local draft when selection changes (e.g. after save)
  useEffect(() => {
    setDraft(selection.actual_amount !== null ? String(selection.actual_amount) : '')
  }, [selection.actual_amount])

  const parsed = draft === '' ? null : parseFloat(draft)
  const dirty = parsed !== selection.actual_amount && !(parsed !== parsed) // skip NaN

  function commit() {
    if (!dirty) return
    void onSave(parsed)
  }
  function discard() {
    setDraft(selection.actual_amount !== null ? String(selection.actual_amount) : '')
  }

  return (
    <div className="row gap-1" style={{ justifyContent: 'flex-end' }}>
      <input
        type="number"
        className="cell-input"
        value={draft}
        placeholder="—"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') discard()
        }}
        style={{ width: 80 }}
      />
      {dirty && (
        <>
          <button className="btn-ghost small" title="Save (Enter)" onClick={commit}>
            ✓
          </button>
          <button className="btn-ghost small" title="Discard (Esc)" onClick={discard}>
            ✕
          </button>
        </>
      )}
    </div>
  )
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className={`stat-card ${alert ? 'alert' : ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  )
}
